import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useChartColors } from './theme';
import { useTooltip } from './ChartTooltip';
import TableView from './TableView';

export interface HBarDatum { label: string; value: number; tooltip?: { label: string; value: string }[]; }
interface HBarsProps { data: HBarDatum[]; height?: number; colorIndex?: number; vFmt?: (n: number) => string; }

export default function HBars({ data, height, colorIndex = 0, vFmt }: HBarsProps) {
    const c = useChartColors();
    const { show, hide, node } = useTooltip();
    const [showTable, setShowTable] = useState(false);

    const rowH = 26, padL = 120, padR = 40, W = 520;
    const H = height ?? (data.length * rowH + 12);
    const max = Math.max(1, ...data.map(d => d.value));
    const plotW = W - padL - padR;
    const fmt = vFmt ?? ((n: number) => String(n));

    return (
        <Box>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="horizontal bar chart">
                {data.map((d, i) => {
                    const y = 6 + i * rowH;
                    const w = plotW * (d.value / max);
                    const truncated = d.label.length > 16;
                    const shown = truncated ? d.label.slice(0, 15) + '…' : d.label;
                    // The axis label truncates at 16 chars, so the tooltip must carry the full
                    // label — otherwise a long customer/item/account name is unreadable anywhere
                    // on the chart. Custom tooltip rows used to replace the label entirely.
                    const tipText = d.tooltip && d.tooltip.length > 0
                        ? `${d.label} — ${d.tooltip.map(t => `${t.label}: ${t.value}`).join(' · ')}`
                        : `${d.label}: ${fmt(d.value)}`;
                    return (
                        <g key={i}
                           onMouseMove={e => show(e.clientX, e.clientY, tipText)}
                           onMouseLeave={hide}>
                            <text x={padL - 8} y={y + rowH / 2} textAnchor="end" dominantBaseline="middle" fontSize={11} fill={c.ink}>
                                {truncated && <title>{d.label}</title>}
                                {shown}
                            </text>
                            <rect x={padL} y={y + 4} width={Math.max(2, w)} height={rowH - 12} rx={3} fill={c.series[colorIndex]} />
                            <text x={padL + Math.max(2, w) + 6} y={y + rowH / 2} dominantBaseline="middle" fontSize={11} fill={c.muted}>{fmt(d.value)}</text>
                        </g>
                    );
                })}
            </svg>
            <Button size="small" onClick={() => setShowTable(s => !s)}>Table view</Button>
            {showTable && <TableView columns={['Label', 'Value']} rows={data.map(d => [d.label, fmt(d.value)])} />}
            {node}
        </Box>
    );
}
